import { CSSProperties, useEffect, useState } from 'react';
import {
    DatePickerFormFieldComponent,
    FieldHeaderComponent,
    InfoPopoverComponent,
    SurveyJsComponent,
    TextAreaFormFieldComponent,
    TextFormFieldComponent,
} from '@vivli/shared/components';
import { Size, Styles } from '@vivli/shared/theme';
import { NarrativeSummaryHelpComponent } from './narrative-summary-help.component';
import { DataRequestHintsConstant, DTIResearchProposalForm } from '@vivli/shared/infrastructure/constants';
import { PurposeOfAnalysisComponent } from '../purpose-of-analysis.component';
import { OutcomesComponent } from '../outcomes.component';
import './surveyJs-custom-style.css';
import { useFormContext } from 'react-hook-form';

import { ISurveyTemplateDto } from '@vivli/shared/infrastructure/interface';
import { useDataRequestContext } from '@vivli/features/data-requests/infrastructure/context';
import { useVivliConfigContext } from '@vivli/core/infrastructure/context';

const containerStyle: CSSProperties = {
    overflowX: 'hidden',
    overflowY: 'auto',
    paddingLeft: Size.INSET_SIZE,
    paddingRight: Size.INSET_SIZE,
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: Size.INSET_SIZE,
};

const formFieldStyle: CSSProperties = {
    marginBottom: '20px',
};

const infoPopupStyle: CSSProperties = {
    minHeight: '30px',
    justifyContent: 'flex-end',
};

const twoColStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'minmax(200px, 400px) minmax(200px, 400px)',
    columnGap: '20px',
};

const fortySixtyColStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '40% 1fr',
    columnGap: '20px',
};

const projectTimelineHint = (
    <div>
        <div>
            <strong>The Average timeline from request submission to data access is 5 months.</strong>
        </div>
        <br />
        <div>All data requests proceed through a data request review.</div>
        <br />
        <div>
            Step 1: Vivli Administrator form check — Ensure all required fields of the data request form are completed and that a qualified
            statistician is part of the team.
        </div>
        <br />
        <div>Step 2: Data Contributor Review — Check feasibility of fulfilling request.</div>
        <br />
        <div>Step 3: Independent Review Panel or Scientific Committee — Reviews based upon the merits of the research proposal.</div>
        <br />
        <div>
            Depending on the studies being requested for reuse, not all three steps will be completed. Please see a{' '}
            <a href="https://vivli.org/members/ourmembers/" target="_blank" rel="noopener noreferrer">
                member's page
            </a>{' '}
            for the specific review process their studies or alternatively the metadata about a specific study.
        </div>
        <br />
        <div>
            Once the data request review is completed, the Data Use Agreement will be executed and the data, if not already stored in the
            Vivli platform, will be anonymized and uploaded for the researcher to access the data package.
        </div>
    </div>
);

const briefDescriptionHint = (
    <div>
        <div>Please provide a brief description of the study design, for example:</div>
        <ul>
            <li>case-control</li>
            <li>cohort</li>
            <li>cross-sectional</li>
            <li>historical controlled</li>
            <li>hybrid designs</li>
            <li>meta-analysis</li>
            <li>pooled analysis</li>
        </ul>
        <div>Please also provide a description of the study population or populations for the proposed research, for example:</div>
        <ul>
            <li>the study arms from the requested clinical studies</li>
            <li>intent-to-treat or per-protocol populations</li>
            <li>the inclusion and exclusion criteria for any cohort or subgroup analysis</li>
        </ul>
        <div>
            Please clarify how the data you have requested will help you answer the hypothesis you have proposed. This includes outlining
            the methods you will use and how you will handle any missing data. Consider providing well defined baselines and SAP details
            that consider as many variables as possible.
        </div>
        <br></br>
        <div>
            If you will be using any data that has not been requested through Vivli for your analysis, please provide details of the data,
            including the NCT ID, study title and sponsor name, and how it will be used in the analysis. If this list is more than 3
            studies, please upload it as an Excel spreadsheet and include this as an additional attachment with your request.
        </div>
    </div>
);

const specificOutcomesHint = (
    <div>
        <div>Please describe the specific outcomes elements and how they will be categorized/defined for your study, including:</div>
        <ul>
            <li>Domain (e.g., anxiety)</li>
            <li>Specific measurement (e.g., Hamilton Anxiety Rating Scale)</li>
            <li>Specific Metric (e.g., change from baseline)</li>
            <li>Method of Aggregation (e.g., mean)</li>
            <li>Time-point(s) (e.g., 3 and 6 months)</li>
        </ul>
        <div>Please describe the endpoints of the requested studies that will be analyzed.</div>
        <br />
        <div>
            Note that some data contributors require that research proposals must relate to the intervention or disease that was the subject
            of the original clinical studies to ensure the use of the data aligns with the informed consent provided by clinical study
            participants. For information about the requirements of members, please review their Members Page.
        </div>
    </div>
);

export const ResearchProposalFormComponent = () => {
    const formApi = useFormContext();
    const { dataRequest, updateDataRequest, setSurveyHasErrors, dateEditingEnabled } = useDataRequestContext();
    const [lastSurveyTemplateItem, setLastSurveyTemplateItem] = useState<ISurveyTemplateDto>();
    const vivliConfig = useVivliConfigContext();

    useEffect(() => {
        const surveyTemplates = vivliConfig?.surveyTemplates;

        const surveyVersionPresent = formApi.getValues('researchProposal.surveyVersion');

        let selectedSurveyTemplate = null;

        // Check if the survey version is present
        if (surveyVersionPresent) {
            selectedSurveyTemplate = surveyTemplates?.find((item) => item.surveyVersion === surveyVersionPresent);
        }

        // If no survey template is found for the version, check for isCurrent: true
        if (!selectedSurveyTemplate) {
            selectedSurveyTemplate = surveyTemplates?.find((item) => item.isCurrent);
        }

        setLastSurveyTemplateItem(selectedSurveyTemplate);

        if (selectedSurveyTemplate) {
            formApi.setValue('researchProposal.surveyVersion', selectedSurveyTemplate.surveyVersion);
        }
    }, [vivliConfig, formApi]);

    const handleSurveyChange = (value) => {
        const jsonString = JSON.stringify(value);
        formApi.setValue('researchProposal.surveyAnswers', jsonString, { shouldDirty: true });
    };

    return (
        <div style={containerStyle}>
            <FieldHeaderComponent title="GENERAL" />

            <TextFormFieldComponent
                name={'requestTitle'}
                label={
                    'Title of the Proposed Research (Overall brief description, including the condition, about the proposed research question that is understandable by a general audience)'
                }
                readonly={false}
                style={formFieldStyle}
                dataId={DTIResearchProposalForm.ResearchProposalTitleTextBox}
                onBlur={(e) => {
                    dataRequest.requestTitle = e.currentTarget.value;
                    updateDataRequest(dataRequest);
                }}
            />

            <InfoPopoverComponent content={<NarrativeSummaryHelpComponent />} size={20} style={infoPopupStyle} />
            <TextAreaFormFieldComponent
                style={formFieldStyle}
                name={'researchProposal.laySummary'}
                label={'Lay Summary of the Research Request'}
                dataId={DTIResearchProposalForm.ResearchProposalSummaryTextBox}
            />

            <TextAreaFormFieldComponent
                style={formFieldStyle}
                name={'researchProposal.specificAimsObjectives'}
                label={'What are the specific aims/objectives of the proposed research, including the specific hypotheses to be evaluated?'}
            />

            <div style={Styles.FORM_ROW}>
                <PurposeOfAnalysisComponent />
                <OutcomesComponent />
            </div>

            <FieldHeaderComponent title="Artificial Intelligence / Machine Learning" />
            <SurveyJsComponent
                answers={formApi.getValues('researchProposal.surveyAnswers')}
                onChange={(value) => handleSurveyChange(value)}
                setSurveyHasErrors={setSurveyHasErrors}
                questions={lastSurveyTemplateItem?.surveyTemplate}
            />

            <FieldHeaderComponent title="STUDY DESIGN" />

            <InfoPopoverComponent content={briefDescriptionHint} size={20} style={infoPopupStyle} />
            <TextAreaFormFieldComponent
                style={formFieldStyle}
                name={'researchProposal.researchProposalStudyDesign.briefDescription'}
                label={'Brief Description'}
                dataId={DTIResearchProposalForm.BriefDescriptionTextBox}
            />

            <InfoPopoverComponent content={specificOutcomesHint} size={20} style={infoPopupStyle} />
            <TextAreaFormFieldComponent
                style={formFieldStyle}
                name={'researchProposal.researchProposalStudyDesign.elementsforOutcomes'}
                label={'Please describe the specific outcomes elements and how they will be categorized/defined for your proposed research'}
                dataId={DTIResearchProposalForm.ResearchProposalStudyDesignTextBox}
            />

            <TextAreaFormFieldComponent
                style={formFieldStyle}
                name={'researchProposal.researchProposalStudyDesign.mainPredictorIndependentVariable'}
                label={'Main Predictor/Independent Variable and how it will be categorized/defined for your proposed research'}
                dataId={DTIResearchProposalForm.ResearchProposalMainPredictorTextBox}
            />

            <TextAreaFormFieldComponent
                style={formFieldStyle}
                name={'researchProposal.researchProposalStudyDesign.otherVariablesOfInterest'}
                label={
                    'Other variables of interest that will be used in your analysis and how they will be categorized/defined in your proposed research (i.e. genders, age groups, ethnic groups)'
                }
                dataId={DTIResearchProposalForm.ResearchProposalOtherVariablesTextBox}
            />

            <FieldHeaderComponent
                title="PROJECT TIMELINE"
                rightAccessory={<InfoPopoverComponent content={projectTimelineHint} size={20} />}
            />

            <div style={fortySixtyColStyle}>
                <DatePickerFormFieldComponent
                    name={'researchProposal.projectTimeline.projectStartDate'}
                    label={'Target Analysis Start Date (at least 5 months from Request creation)'}
                    readonly={!dateEditingEnabled}
                />

                <DatePickerFormFieldComponent
                    name={'researchProposal.projectTimeline.analysisCompletionDate'}
                    label={'Estimated Analysis Completion Date (at least 12 months from Target Start date)'}
                    readonly={!dateEditingEnabled}
                />
            </div>

            <FieldHeaderComponent title="DISSEMINATION AND PUBLICATION PLAN" />

            <div style={fortySixtyColStyle}>
                <TextAreaFormFieldComponent
                    name={'researchProposal.disseminationAndPublicationPlan'}
                    label={'Dissemination & Publication Plan'}
                    hint={DataRequestHintsConstant.researchProposal.disseminationAndPublicationPlan}
                    dataId={DTIResearchProposalForm.ResearchProposalDisseminationPlanTextBox}
                />

                <TextAreaFormFieldComponent
                    name={'researchProposal.citations'}
                    label={'Provide references for all cited material (following APA guidelines)'}
                />
            </div>
        </div>
    );
};
